import type { RouteRecordRaw } from 'vue-router'
import { ROLE_GROUPS } from '../../constants/roleGroups'

const PlaceholderView = () => import('../../views/admin/stubs/AdminPlaceholderView.vue')

export const adminPlatformRoutes: RouteRecordRaw[] = [
  {
    path: 'clients',
    name: 'admin.clients.index',
    component: () => import('../../views/admin/clients/ClientsListView.vue'),
    meta: {
      title: 'Clients',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMINS,
      orAnyPermissions: ['admin.clients.view'],
    },
  },
  {
    path: 'clients/:clientId',
    name: 'admin.clients.detail',
    component: () => import('../../views/clients/ClientDetailView.vue'),
    meta: {
      title: 'Client Details',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMINS,
      orAnyPermissions: ['admin.clients.view'],
    },
  },
  {
    path: 'clients/:clientId/onboarding',
    name: 'admin.clients.onboarding',
    component: () => import('../../views/clients/ClientDetailView.vue'),
    meta: {
      title: 'Client Onboarding Workspace',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMINS,
      orAnyPermissions: ['admin.clients.view'],
    },
  },
  {
    path: 'clients/:clientId/invoices/:invoiceId',
    name: 'admin.clients.invoice',
    redirect: (to) => ({
      name: 'admin.billing.invoice-detail',
      params: { invoiceId: String(to.params.invoiceId || '') },
      query: {
        ...(to.query || {}),
        client_id: String(to.params.clientId || ''),
        from: 'client',
      },
    }),
    meta: {
      title: 'Client Invoice Detail',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMIN_PANEL,
    },
  },
  {
    path: 'reports/client-status',
    name: 'admin.reports.client-status',
    component: () => import('../../views/admin/reports/ClientStatusReportView.vue'),
    meta: {
      title: 'Client Status Report',
      requiresAuth: true,
      requireAnyPermissions: ['admin.clients.view'],
    },
  },
  {
    path: 'billing',
    name: 'admin.billing.index',
    component: () => import('../../views/admin/billing/BillingOverviewView.vue'),
    meta: {
      title: 'Billing',
      requiresAuth: true,
      requireAnyPermissions: ['admin.billing.view'],
    },
  },
  {
    path: 'billing/subscriptions',
    name: 'admin.billing.subscriptions',
    component: () => import('../../views/admin/billing/BillingSubscriptionsView.vue'),
    meta: {
      title: 'Billing Subscriptions',
      requiresAuth: true,
      requireAnyPermissions: ['admin.billing.subscriptions.view'],
    },
  },
  {
    path: 'billing/invoices',
    alias: ['billing/charges'],
    name: 'admin.billing.invoices',
    component: () => import('../../views/admin/billing/BillingChargesView.vue'),
    meta: {
      title: 'Billing Invoices',
      requiresAuth: true,
      requireAnyPermissions: ['admin.billing.view'],
    },
  },
  {
    path: 'billing/charges',
    name: 'admin.billing.charges',
    redirect: { name: 'admin.billing.invoices' },
    meta: {
      title: 'Billing Invoices',
      requiresAuth: true,
      requireAnyPermissions: ['admin.billing.view'],
    },
  },
  {
    path: 'billing/invoices/:invoiceId',
    name: 'admin.billing.invoice-detail',
    component: () => import('../../views/admin/billing/BillingInvoiceDetailView.vue'),
    meta: {
      title: 'Billing Invoice Detail',
      requiresAuth: true,
      requireAnyPermissions: ['admin.billing.view'],
    },
  },
  {
    path: 'billing/payments',
    name: 'admin.billing.payments',
    component: () => import('../../views/admin/billing/BillingPaymentsView.vue'),
    meta: {
      title: 'Billing Payments',
      requiresAuth: true,
      requireAnyPermissions: ['admin.billing.payments.view'],
    },
  },
  {
    path: 'billing/payments/:paymentId',
    name: 'admin.billing.payment-detail',
    component: () => import('../../views/admin/billing/BillingPaymentDetailView.vue'),
    meta: {
      title: 'Billing Payment Detail',
      requiresAuth: true,
      requireAnyPermissions: ['admin.billing.payments.view'],
    },
  },
  {
    path: 'billing/extra-pricing',
    name: 'admin.billing.extra-pricing',
    component: () => import('../../views/admin/billing/BillingExtraPricingView.vue'),
    meta: {
      title: 'Extra Pricing',
      requiresAuth: true,
      requireAnyPermissions: ['core.billing_extra_pricing.view'],
    },
  },
  {
    path: 'support',
    name: 'admin.support.index',
    component: PlaceholderView,
    meta: {
      title: 'Support',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMIN_PANEL,
      stubMessage: 'Support queue and ticket orchestration.',
      todo: [
        'Connect ticket list and detail views.',
        'Add status, SLA and escalation controls.',
        'Bridge support actions into activity feed.',
      ],
    },
  },
  {
    path: 'support/knowledge',
    name: 'admin.support.knowledge',
    component: () => import('../../views/admin/help-center/HelpCenterPortalPreviewView.vue'),
    meta: {
      title: 'Knowledge Base',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMIN_PANEL,
    },
  },
  {
    path: 'support/knowledge/category/:id',
    name: 'admin.support.knowledge.category',
    component: () => import('../../views/admin/help-center/HelpCenterPortalCategoryView.vue'),
    meta: {
      title: 'Knowledge Base Category',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMIN_PANEL,
    },
  },
  {
    path: 'support/knowledge/article/:id',
    name: 'admin.support.knowledge.article',
    component: () => import('../../views/admin/help-center/HelpCenterPortalArticleView.vue'),
    meta: {
      title: 'Knowledge Base Article',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMIN_PANEL,
    },
  },
  {
    path: 'support/feedback',
    name: 'admin.support.feedback',
    component: () => import('../../views/admin/support/FeedbackListView.vue'),
    meta: {
      title: 'Learner Feedback',
      requiresAuth: true,
      requireRoles: ROLE_GROUPS.ADMIN_PANEL,
    },
  },
]
